/**
 * Which logo in `public/brand-logos/` belongs to a registration's `merk`, if any.
 *
 * A brand outside the set gets no logo, and the header looks as it does without one; nothing is
 * drawn in its place.
 *
 * **Every file is from Wikimedia Commons, which only hosts material that is free to reuse.**
 * `public/brand-logos/SOURCES.txt` records the page and licence of each one, and travels with the
 * files into `dist/` for the same reason the plate font's licence does. A brand whose emblem
 * Commons does not carry is left out rather than sourced elsewhere. Detailed crests are protected
 * by copyright as well as trademark, which is why Alfa Romeo, Scania, Porsche and Ferrari are not
 * on Commons as emblems.
 *
 * **The logo is the badge, never the brand's name set in its typeface.** The heading beside it
 * already spells the make, and a wordmark there reads "PEUGEOT PEUGEOT PARTNER". So a brand whose
 * only free file is a wordmark (Peugeot, Škoda, Jaguar, Lancia, Chrysler, Saab, and the two above)
 * has no logo. The exceptions are brands whose badge on the car is the lettering itself: Kia, DAF,
 * Iveco, Jeep, BYD, Lynk & Co. Several files combine the emblem with the name; their `viewBox` is
 * cropped to the emblem, noted per file in `SOURCES.txt`.
 *
 * The logos are trademarks whatever their copyright status. Showing one to say which brand a
 * vehicle is counts as referential use, which the footer's line about trademark ownership and no
 * affiliation is there to keep clear.
 */

const LOGOS: ReadonlySet<string> = new Set([
  'audi', 'bentley', 'bmw', 'byd', 'cadillac', 'chevrolet', 'citroen', 'cupra', 'dacia', 'daf',
  'daihatsu', 'dodge', 'ds', 'fiat', 'ford', 'honda', 'hyundai', 'iveco', 'jeep', 'kia', 'land-rover',
  'leapmotor', 'lexus', 'lynk-co', 'man', 'maserati', 'mazda', 'mercedes-benz', 'mg', 'mini',
  'mitsubishi', 'nissan', 'opel', 'polestar', 'renault', 'seat', 'smart', 'subaru', 'suzuki', 'tesla',
  'toyota', 'volkswagen', 'volvo', 'xpeng', 'zeekr',
])

/**
 * Register spellings that are the same brand as another slug. Measured against the register's own
 * `merk` counts: `VW`, `TESLA MOTORS` and `BMW I` each cover thousands of cars that would otherwise
 * get no logo.
 */
const ALIASES: Readonly<Record<string, string>> = {
  vw: 'volkswagen',
  'tesla-motors': 'tesla',
  'bmw-i': 'bmw',
  'mercedes-amg': 'mercedes-benz',
  'ford-cng-technik': 'ford',
}

/**
 * The logos are car badges. A Honda motorcycle carries a winged emblem rather than the H, so two-
 * and three-wheelers get none rather than a badge their own tank does not wear.
 */
const KINDS_WITHOUT_LOGO: ReadonlySet<string> = new Set(['motorfiets', 'bromfiets', 'driewielig motorrijtuig'])

/** `LYNK&CO` → `lynk-co`, `CITROËN` → `citroen`, `LAND ROVER` → `land-rover`. */
export function brandSlug(brand: string): string {
  return brand
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

/** The logo's path under the site root, or `undefined` when there is none to show. */
export function brandLogoPath(brand: string | undefined, vehicleKind: string | undefined): string | undefined {
  if (!brand) return undefined
  if (vehicleKind && KINDS_WITHOUT_LOGO.has(vehicleKind.trim().toLowerCase())) return undefined

  const slug = brandSlug(brand)
  const resolved = ALIASES[slug] ?? slug
  return LOGOS.has(resolved) ? `/brand-logos/${resolved}.svg` : undefined
}
